declare global {
    interface Window {
        embedded25?: {
            fromConfig: { create: (instanceId: string, configToken: string) => unknown };
            clearEventListeners: () => void;
        };
    }
}

export namespace EmbeddedContent {
    export type Shape = "landscape" | "portrait" | "post";

    export type EmbedTarget =
        | { kind: "iframe"; url: string; shape: Shape }
        | { kind: "embedded25"; instanceId: string; configToken: string; targetId: string };

    const EMBEDDED25_CREATE_CALL = /embedded25\.fromConfig\.create\(\s*(["'])([^"']*)\1\s*,\s*(["'])([^"']*)\3\s*\)/;
    const SAFE_TOKEN = /^[\w-]+$/;

    /** Classifies a pasted URL or embed code. Pasted markup is only read, never rendered or executed. */
    export function parse(input: string): EmbedTarget | undefined {
        const trimmedInput = input.trim();
        if (!trimmedInput) return undefined;
        if (trimmedInput.includes("embedded25.")) return parseEmbedded25(trimmedInput);
        if (!trimmedInput.startsWith("<")) return toEmbedUrl(trimmedInput);

        const pastedDocument = new DOMParser().parseFromString(trimmedInput, "text/html");
        const embedUrl = pastedDocument.querySelector("iframe")?.getAttribute("src") ?? postPermalink(pastedDocument);
        return embedUrl ? toEmbedUrl(embedUrl) : undefined;
    }

    function parseEmbedded25(snippet: string): EmbedTarget | undefined {
        const createCall = EMBEDDED25_CREATE_CALL.exec(snippet);
        if (!createCall) return undefined;
        const instanceId = createCall[2];
        const configToken = createCall[4];
        const pastedDocument = new DOMParser().parseFromString(snippet, "text/html");
        const targetId = pastedDocument.body.querySelector("[id]")?.id ?? "";
        if (![instanceId, configToken, targetId].every((value) => SAFE_TOKEN.test(value))) return undefined;
        return { kind: "embedded25", instanceId, configToken, targetId };
    }

    /** Post URL from the official Instagram, Threads, X, or TikTok blockquote embed codes. */
    function postPermalink(pastedDocument: Document): string | undefined {
        const instagramPermalink = pastedDocument
            .querySelector("blockquote.instagram-media")
            ?.getAttribute("data-instgrm-permalink");
        if (instagramPermalink) return instagramPermalink;

        const threadsPermalink = pastedDocument
            .querySelector("blockquote.text-post-media")
            ?.getAttribute("data-text-post-permalink");
        if (threadsPermalink) return threadsPermalink;

        const tweetLinks = pastedDocument.querySelectorAll("blockquote.twitter-tweet a[href]");
        const tweetPermalink = tweetLinks[tweetLinks.length - 1]?.getAttribute("href");
        if (tweetPermalink) return tweetPermalink;

        return pastedDocument.querySelector("blockquote.tiktok-embed")?.getAttribute("cite") ?? undefined;
    }

    /**
     * Only cross-origin http(s) URLs are embedded: the iframe sandbox allows scripts and same-origin,
     * so a same-origin page could remove its own sandbox. Known video and social page links become
     * their embeddable player URLs.
     */
    export function toEmbedUrl(rawUrl: string): EmbedTarget | undefined {
        let parsed: URL;
        try {
            parsed = new URL(rawUrl.trim());
        } catch {
            return undefined;
        }
        if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return undefined;
        if (parsed.origin === window.location.origin) return undefined;

        const hostname = parsed.hostname.toLowerCase().replace(/^(www|m)\./, "");
        const pathSegments = parsed.pathname.split("/").filter(Boolean);
        const [firstSegment, secondSegment, thirdSegment] = pathSegments;
        const iframe = (url: string, shape: Shape): EmbedTarget => ({ kind: "iframe", url, shape });

        switch (hostname) {
            case "youtube.com": {
                const videoId =
                    firstSegment === "watch"
                        ? parsed.searchParams.get("v")
                        : firstSegment === "shorts" || firstSegment === "embed"
                          ? secondSegment
                          : undefined;
                if (videoId) {
                    const shape = firstSegment === "shorts" ? "portrait" : "landscape";
                    return iframe(`https://www.youtube.com/embed/${encodeURIComponent(videoId)}`, shape);
                }
                break;
            }
            case "youtu.be":
                if (firstSegment)
                    return iframe(`https://www.youtube.com/embed/${encodeURIComponent(firstSegment)}`, "landscape");
                break;
            case "vimeo.com":
                if (/^\d+$/.test(firstSegment ?? "")) {
                    return iframe(`https://player.vimeo.com/video/${firstSegment}`, "landscape");
                }
                break;
            case "tiktok.com": {
                const videoIndex = pathSegments.indexOf("video");
                const videoId = videoIndex !== -1 ? pathSegments[videoIndex + 1] : undefined;
                if (firstSegment?.startsWith("@") && videoId) {
                    return iframe(`https://www.tiktok.com/embed/v2/${encodeURIComponent(videoId)}`, "portrait");
                }
                if (firstSegment === "embed") return iframe(parsed.href, "portrait");
                break;
            }
            case "instagram.com":
                if ((firstSegment === "p" || firstSegment === "reel" || firstSegment === "tv") && secondSegment) {
                    const postType = firstSegment === "reel" ? "reel" : "p";
                    return iframe(
                        `https://www.instagram.com/${postType}/${encodeURIComponent(secondSegment)}/embed`,
                        "post",
                    );
                }
                break;
            case "threads.com":
            case "threads.net":
                if (
                    /^@[\w.]+$/.test(firstSegment ?? "") &&
                    secondSegment === "post" &&
                    SAFE_TOKEN.test(thirdSegment ?? "")
                ) {
                    return iframe(`https://www.${hostname}/${firstSegment}/post/${thirdSegment}/embed`, "post");
                }
                break;
            case "twitter.com":
            case "x.com":
                if (secondSegment === "status" && /^\d+$/.test(thirdSegment ?? "")) {
                    return iframe(`https://platform.twitter.com/embed/Tweet.html?id=${thirdSegment}`, "post");
                }
                break;
            case "platform.twitter.com":
                return iframe(parsed.href, "post");
            case "facebook.com": {
                if (firstSegment === "plugins") return iframe(parsed.href, "post");
                const isVideo = pathSegments.includes("videos") || firstSegment === "watch" || firstSegment === "reel";
                const isPost =
                    pathSegments.includes("posts") || firstSegment === "permalink.php" || firstSegment === "photo";
                const encodedHref = encodeURIComponent(parsed.href);
                if (isVideo) {
                    return iframe(`https://www.facebook.com/plugins/video.php?href=${encodedHref}`, "landscape");
                }
                if (isPost) return iframe(`https://www.facebook.com/plugins/post.php?href=${encodedHref}`, "post");
                break;
            }
        }
        return iframe(parsed.href, "landscape");
    }
}
